import { useAppSelector } from '@hooks/useRedux';
import { useSyncStatus } from '@hooks/useSyncStatus';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { AlertTriangle, Mail, RefreshCw, Wifi } from 'lucide-react';
import { ensureOutletSync } from '@/renderer/lib/powersync/ensureOutletSync';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useState } from 'react';

import { SyncStatusChip } from './SyncStatusChip';

// Mirrors src/main/menu.ts's SUPPORT_EMAIL — no shared renderer/main constant
// exists for this today, and it's a single, rarely-changing value.
const SUPPORT_EMAIL = 'samuel@prodevkampala.com';

// A normal first-time sync (small/organic outlet) completes in single-digit
// seconds. These are defense-in-depth against any failure mode that doesn't
// trip the main-process retry breaker (see powersyncHandlers.ts) — that
// breaker already stops repeated identical PSYNC_S*-class errors after 3
// attempts (~15s) and surfaces them via the sync-engine-failed banner instead
// of this overlay, so by the time SLOW_THRESHOLD_MS is reached here, we're
// either genuinely still working or facing an error type the breaker doesn't
// recognize — both worth surfacing rather than spinning silently.
const SLOW_THRESHOLD_MS = 20_000;
const STUCK_THRESHOLD_MS = 60_000;

type Stage = 'initial' | 'slow' | 'stuck';

/**
 * Full-screen overlay shown while PowerSync completes its very first sync
 * after login. Once `hasSynced` flips to true the overlay unmounts and the
 * user can interact with their fully-loaded data.
 *
 * Condition: show when PowerSync is connected/connecting but has never
 * completed a sync on this device. Hidden for returning users whose local
 * SQLite already has data from a previous session.
 *
 * Mounted once in App.tsx (z-40 — below SplashScreen z-50, above all
 * page content).
 */
export const InitialSyncOverlay: React.FC = () => {
  const { hasSynced, connected, connecting, downloadError, uploadError } =
    useSyncStatus();
  const currentUser = useAppSelector(selectCurrentUser);
  const [stage, setStage] = useState<Stage>('initial');

  const shouldShow = !hasSynced && (connected || connecting);
  const syncError = downloadError ?? uploadError;

  // All hooks must run unconditionally (before any early return) — see Rules
  // of Hooks. The effect itself is the thing that's conditional.
  useEffect(() => {
    if (!shouldShow) {
      setStage('initial');
      return undefined;
    }
    const slowTimer = setTimeout(() => setStage('slow'), SLOW_THRESHOLD_MS);
    const stuckTimer = setTimeout(() => setStage('stuck'), STUCK_THRESHOLD_MS);
    return () => {
      clearTimeout(slowTimer);
      clearTimeout(stuckTimer);
    };
  }, [shouldShow]);

  // The real sync error text is technical/backend-specific (e.g. a raw
  // Postgres constraint message) and not something a user can act on —
  // send it to Sentry for diagnosis and show a fixed, friendly message here
  // instead. Keyed on `syncError` so this only fires once per distinct error,
  // not on every status poll.
  useEffect(() => {
    if (syncError) {
      Sentry.captureMessage(`Initial sync failed: ${syncError}`, 'error');
    }
  }, [syncError]);

  const handleRetry = useCallback(() => {
    setStage('initial');
    ensureOutletSync(currentUser?.outlet_id);
  }, [currentUser?.outlet_id]);

  if (!shouldShow) return null;

  const isEscalated = Boolean(syncError) || stage === 'stuck';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-white/95 backdrop-blur-sm">
      <div className="mx-4 flex w-full max-w-sm flex-col items-center gap-6 rounded-2xl border border-gray-100 bg-white px-8 py-10 shadow-2xl">
        {/* Icon */}
        <div
          className={[
            'flex h-16 w-16 items-center justify-center rounded-full',
            isEscalated ? 'bg-red-50' : 'bg-pos-primary/10',
          ].join(' ')}
        >
          {isEscalated ? (
            <AlertTriangle className="h-8 w-8 text-red-500" />
          ) : (
            <RefreshCw className="h-8 w-8 animate-spin text-pos-primary" />
          )}
        </div>

        {/* Headline */}
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEscalated
              ? "We're having trouble syncing"
              : 'Offline Sync Setup…'}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            {syncError
              ? "We couldn't finish syncing your data. Try retrying, or contact support if it continues."
              : stage === 'stuck'
                ? 'This is taking much longer than usual. There may be a connection issue — try retrying, or contact support if it continues.'
                : stage === 'slow'
                  ? 'This is taking longer than usual… hang tight.'
                  : "We're setting up your account for offline use. This only happens once."}
          </p>
        </div>

        {isEscalated ? (
          <div className="flex w-full flex-col gap-2">
            <button
              type="button"
              onClick={handleRetry}
              className="flex items-center justify-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90"
            >
              <RefreshCw size={15} />
              Retry
            </button>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50"
            >
              <Mail size={13} />
              Contact support
            </a>
          </div>
        ) : (
          <>
            {/* Animated progress dots */}
            <div className="flex items-center gap-1.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-2 w-2 rounded-full bg-pos-primary"
                  style={{ animation: `bounce 1.2s ${i * 0.2}s infinite` }}
                />
              ))}
            </div>

            {/* Live connection chip */}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Wifi size={13} />
              <span>Syncing via</span>
              <SyncStatusChip />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
